/**
 * ResultsList Component
 * List container for search results with conditional rendering
 * Now supports ViewControls and configurable layouts
 */

import React, { useMemo } from 'react';
import { useWidgetStore } from '../store/widgetStore';
import { useViewStore } from '../store/viewStore';
import { ViewControls } from '../modules/controls';
import { ChatResponse } from './ChatResponse';
import { SearchResultCard } from './SearchResultCard';
import { SearchingAnimation } from './loading';
import { getWordPressSettings } from '../utils/settings';
import type { WidgetConfig } from '../types';
import './ResultsList.css';
import '../modules/controls/styles/controls.css';

interface ResultsListProps {
  onResultClick?: (url: string) => void;
  config?: WidgetConfig;
}

export const ResultsList: React.FC<ResultsListProps> = ({ onResultClick, config }) => {
  const { results, responseType, isSearching } = useWidgetStore();
  const { displayMode, gridColumns, showTrustScore, showTags, showActions } = useViewStore();

  // Read WordPress configurations
  const wpSettings = useMemo(() => getWordPressSettings(), []);
  
  // Merge with config prop
  const mergedConfig = useMemo(() => {
    return {
      ...wpSettings,
      ...config,
    };
  }, [wpSettings, config]);

  // Get user controls config
  const userControlsConfig = useMemo(() => {
    return mergedConfig?.userControls || wpSettings?.userControls || {
      showControlsBar: true,
      allowViewChange: true,
      availableViews: ['list', 'cards', 'text'],
      allowColumnsChange: true,
      availableColumns: [1, 2, 3],
      showSettingsMenu: true,
      availableSettings: ['trustscore', 'tags', 'actions', 'compact'],
      rememberUserPreferences: true,
    };
  }, [mergedConfig, wpSettings]);

  if (isSearching) {
    return <SearchingAnimation />;
  }

  if (!results.length) {
    return null;
  }

  // If response type is chat, or if we have a single chat-type result, show as chat
  if (responseType === 'chat' || (results.length === 1 && results[0].type === 'chat')) {
    return <ChatResponse content={results[0].content || results[0].snippet} isLoading={false} />;
  }

  // Determine layout class based on display mode and grid columns
  const getLayoutClass = () => {
    if (displayMode === 'list') {
      return 'ai360-results-list-view';
    }
    if (displayMode === 'text') {
      return 'ai360-results-text-view';
    }
    // Cards mode with grid
    if (gridColumns === 3) {
      return 'ai360-results-grid-3col';
    }
    if (gridColumns === 2) {
      return 'ai360-results-grid-2col';
    }
    return 'ai360-results-grid-1col';
  };

  // Otherwise, show as search results with controls
  return (
    <div className="ai360-results-container">
      {/* View Controls */}
      <ViewControls config={userControlsConfig} />

      {/* Results Grid/List */}
      <div className={`ai360-results-list ${getLayoutClass()}`}>
        {results.map((result) => (
          <SearchResultCard
            key={result.id}
            result={result}
            viewMode={displayMode === 'list' ? 'list' : 'card'}
            onNavigate={onResultClick}
            showTrustScore={showTrustScore}
            showTags={showTags}
            showActions={showActions}
          />
        ))}
      </div>
    </div>
  );
};
